import React, { useState, useEffect } from 'react';
import { Mail, Trash2, Download, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const NewsletterManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/newsletter-subscriptions`);
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subId) => {
    if (!window.confirm('Are you sure you want to remove this subscriber?')) {
      return;
    }

    try {
      await axios.delete(`${API}/newsletter-subscriptions/${subId}`);
      toast.success('Subscriber removed successfully!');
      fetchSubscribers();
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      toast.error('Failed to remove subscriber');
    }
  };

  const exportToCSV = () => {
    if (subscribers.length === 0) {
      toast.error('No subscribers to export');
      return;
    }

    const headers = ['Name', 'Email', 'Subscribed Date'];
    const rows = subscribers.map(sub => [
      sub.name,
      sub.email,
      new Date(sub.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Subscribers exported!');
  };

  const copyAllEmails = () => {
    const emails = subscribers.map(sub => sub.email).join(', ');
    navigator.clipboard.writeText(emails);
    toast.success('All emails copied to clipboard!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Newsletter Subscribers</h2>
          <p className="text-gray-400 text-sm">
            {subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={copyAllEmails}
            disabled={subscribers.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail size={16} />
            <span>Copy Emails</span>
          </button>
          <button
            onClick={exportToCSV}
            disabled={subscribers.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={16} />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Subscribers List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-4">Loading subscribers...</p>
        </div>
      ) : subscribers.length === 0 ? (
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-12 text-center">
          <Users className="text-purple-400 mx-auto mb-4" size={48} />
          <p className="text-gray-400">No newsletter subscribers yet</p>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl overflow-hidden">
          {/* Table Header */}
          <div className="bg-black/30 px-6 py-4 grid grid-cols-12 gap-4 text-sm font-semibold text-gray-400 border-b border-purple-500/20">
            <div className="col-span-4">Name</div>
            <div className="col-span-5">Email</div>
            <div className="col-span-2">Subscribed</div>
            <div className="col-span-1 text-right">Action</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-purple-500/10">
            {subscribers.map((sub) => (
              <div
                key={sub.id}
                className="px-6 py-4 grid grid-cols-12 gap-4 items-center hover:bg-purple-900/10 transition-colors"
              >
                <div className="col-span-4">
                  <p className="text-white font-medium">{sub.name}</p>
                </div>
                <div className="col-span-5">
                  <a
                    href={`mailto:${sub.email}`}
                    className="text-gray-300 hover:text-purple-400 transition-colors text-sm"
                  >
                    {sub.email}
                  </a>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-400 text-sm">
                    {new Date(sub.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div className="col-span-1 text-right">
                  <button
                    onClick={() => handleDelete(sub.id)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-all"
                    title="Delete subscriber"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterManagement;
